import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useIntl } from "react-intl";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { CameraAlt } from "@material-ui/icons";
import { Divider, Grid, Avatar, CircularProgress } from "@material-ui/core";
import { checkFileType, checkFileSize } from "utils/fileValidation";
import useStyles from "./accountStyles";
import translate from "../../../i18n/translate";
import translateInt from "../../../i18n/translateIntl";
import {
  SrAlert,
  SrButton,
  SrInput,
  Typography,
  SrLabel,
  SrCustomFileInput,
} from "../../common";
import { BUSINESS_RECEIPT } from "../../../constants/api";
import { validations } from "utils";

const Receipt = () => {
  const classes = useStyles();
  const intl = useIntl();

  const initialValuesFactory = (values) => ({
    businessName: values?.business_name,
    street: values?.address.street,
    exterior: values?.address.numberExt,
    interior: values?.address.numberInt,
    zip: values?.address.zip_code,
    colony: values?.address.town,
    state: values?.address.city,
    phone: values?.phone.number,
    website: values?.webpage,
    facebook: values?.facebook,
    twitter: values?.twitter,
    additionalInfo: values?.extra,
    logo: values?.logo,
  });

  const validationSchema = Yup.object().shape({
    businessName: Yup.string().required(
      intl.formatMessage({ id: "common.requiredField" })
    ),
    street: Yup.string(),
    exterior: Yup.string(),
    interior: Yup.string(),
    zip: validations.zipCode,
    colony: Yup.string(),
    state: Yup.string(),
    phone: validations.phone,
    website: Yup.string(),
    facebook: Yup.string(),
    twitter: Yup.string(),
    additionalInfo: Yup.string(),
    logo: Yup.object()
      .nullable()
      .shape({
        file: Yup.mixed()
          .test(
            "",
            intl.formatMessage({ id: "common.errors.onlyImageFormat" }),
            (value) => (value ? checkFileType(value) : true)
          )
          .test(
            "",
            intl.formatMessage(
              { id: "common.errors.fileMaxSize" },
              { size: "600", unit: "KB" }
            ),
            (value) => (value ? checkFileSize(value, 600) : true)
          ),
      }),
  });

  const [uploadPicture, setUploadPicture] = useState(null);
  const [profilePhotoSelected, setprofilePhotoSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState(initialValuesFactory());
  const [error, setError] = useState();
  const [fetchDataError, setFetchDataError] = useState();

  useEffect(() => {
    setLoading(false);
    setFetchDataError();

    const fetchData = async () => {
      try {
        const response = await axios.get(BUSINESS_RECEIPT);
        const data = response.data.result;

        setInitialValues(initialValuesFactory(data));
        setUploadPicture(`data:image/jpeg;base64,${data.logo}`);
        setprofilePhotoSelected(data.logo);
      } catch {
        setFetchDataError("Error cargando la configuracion de recibo");
      }

      setLoading(true);
    };

    fetchData();
  }, []);

  const handlePictureUpload = (event, setFieldValue) => {
    const [pictureSelected] = event.target.files;

    if (!pictureSelected) {
      setFieldValue("logo", "");
      setUploadPicture(`data:image/jpeg;base64,${initialValues.logo}`);
    } else {
      setFieldValue("logo", {
        file: pictureSelected,
      });

      const reader = new FileReader();
      reader.readAsDataURL(pictureSelected);

      reader.onload = () => {
        const base64 = reader.result;
        const [, base64Code] = base64.split(",");
        setprofilePhotoSelected(base64Code);
      };

      setUploadPicture(URL.createObjectURL(pictureSelected));
    }
  };

  const onSubmit = async (values) => {
    setError();

    const data = {
      business_name: values.businessName,
      address: {
        street: values.street,
        numberExt: values.exterior,
        numberInt: values.interior,
        zip_code: values.zip,
        town: values.colony,
        city: values.state,
      },
      phone: {
        number: values.phone,
      },
      webpage: values.website,
      facebook: values.facebook,
      twitter: values.twitter,
      extra: values.additionalInfo,
      logo: profilePhotoSelected,
    };

    try {
      const response = await axios.post(BUSINESS_RECEIPT, data);
      toast.success(translate("account.receipt.dataSavedSuccess"));

      return response;
    } catch (error) {
      setError(translate("account.receipt.dataSavedError"));
    }
  };

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h5">
            {translate("account.receipt.title")}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography>{translate("account.receipt.body")}</Typography>
        </Grid>
        {fetchDataError && (
          <Grid item xs={12}>
            <SrAlert severity="error">{fetchDataError}</SrAlert>
          </Grid>
        )}
        <Grid item xs={12}>
          {loading ? (
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
            >
              {({
                errors,
                handleBlur,
                handleChange,
                touched,
                values,
                handleSubmit,
                isSubmitting,
                setFieldValue,
              }) => (
                <Form onSubmit={handleSubmit}>
                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12}>
                      <SrCustomFileInput
                        name="logo"
                        icon={<CameraAlt />}
                        onChange={(event) =>
                          handlePictureUpload(event, setFieldValue)
                        }
                        onBlur={handleBlur}
                        helperText={touched.logo && errors.logo?.file}
                        error={errors.logo?.file && touched.logo}
                        label={translate("account.receipt.logo")}
                      >
                        <Avatar
                          className={classes.avatar}
                          src={uploadPicture}
                          alt={intl.formatMessage({
                            id: "account.receipt.avatarAlt",
                          })}
                        />
                      </SrCustomFileInput>
                    </Grid>
                    <Grid item xs={12}>
                      <SrInput
                        variant="outlined"
                        label={translateInt("account.receipt.businessName")}
                        placeholder={translateInt("common.entry.myBusiness")}
                        name="businessName"
                        type="text"
                        value={values.businessName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(
                          errors.businessName && touched.businessName
                        )}
                        helperText={touched.businessName && errors.businessName}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Divider />
                    </Grid>
                    <Grid item xs={12}>
                      <SrInput
                        variant="outlined"
                        label={translate("common.entry.street")}
                        placeholder={translateInt("common.entry.street")}
                        name="street"
                        type="text"
                        value={values.street}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(errors.street && touched.street)}
                        helperText={touched.street && errors.street}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <SrInput
                        variant="outlined"
                        label={translate("common.entry.exteriorNum")}
                        placeholder={translateInt("common.entry.exteriorNum")}
                        name="exterior"
                        type="text"
                        value={values.exterior}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(errors.exterior && touched.exterior)}
                        helperText={touched.exterior && errors.exterior}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <SrInput
                        variant="outlined"
                        label={translate("common.entry.interiorNum")}
                        placeholder={translateInt("common.entry.interiorNum")}
                        name="interior"
                        type="text"
                        value={values.interior}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(errors.interior && touched.interior)}
                        helperText={touched.interior && errors.interior}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <SrInput
                        variant="outlined"
                        label={translate("common.entry.zip")}
                        placeholder={translateInt("common.entry.zip")}
                        name="zip"
                        type="text"
                        value={values.zip}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(errors.zip && touched.zip)}
                        helperText={touched.zip && errors.zip}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <SrInput
                        variant="outlined"
                        label={translate("common.entry.suburb")}
                        placeholder={translateInt("common.entry.suburb")}
                        name="colony"
                        type="text"
                        value={values.colony}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(errors.colony && touched.colony)}
                        helperText={touched.colony && errors.colony}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <SrInput
                        variant="outlined"
                        label={translate("common.entry.state")}
                        placeholder={translateInt("common.entry.state")}
                        name="state"
                        type="text"
                        value={values.state}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(errors.state && touched.state)}
                        helperText={touched.state && errors.state}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Divider />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <SrLabel>{translate("common.entry.phone")}</SrLabel>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <SrInput
                        variant="outlined"
                        placeholder={translateInt("common.entry.phone")}
                        name="phone"
                        type="tel"
                        value={values.phone}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(errors.phone && touched.phone)}
                        helperText={touched.phone && errors.phone}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <SrLabel>{translate("common.entry.website")}</SrLabel>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <SrInput
                        variant="outlined"
                        placeholder={translateInt("common.entry.website")}
                        name="website"
                        type="text"
                        value={values.website}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(errors.website && touched.website)}
                        helperText={touched.website && errors.website}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <SrLabel>{translate("common.entry.facebook")}</SrLabel>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <SrInput
                        variant="outlined"
                        placeholder={translateInt("common.entry.facebookLink")}
                        name="facebook"
                        type="text"
                        value={values.facebook}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(errors.facebook && touched.facebook)}
                        helperText={touched.facebook && errors.facebook}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <SrLabel>{translate("common.entry.twitter")}</SrLabel>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <SrInput
                        variant="outlined"
                        placeholder={translateInt("common.entry.username")}
                        name="twitter"
                        type="text"
                        value={values.twitter}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(errors.twitter && touched.twitter)}
                        helperText={touched.twitter && errors.twitter}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Divider />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <SrLabel>
                        {translate("common.entry.additionalInfoLabel")}
                      </SrLabel>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <SrInput
                        variant="outlined"
                        placeholder={translateInt(
                          "common.entry.additionalInfo"
                        )}
                        name="additionalInfo"
                        type="text"
                        value={values.additionalInfo}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(
                          errors.additionalInfo && touched.additionalInfo
                        )}
                        helperText={
                          touched.additionalInfo && errors.additionalInfo
                        }
                        multiline
                        rows={4}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Divider />
                    </Grid>
                    {error && (
                      <Grid item xs={12}>
                        <SrAlert severity="error">{error}</SrAlert>
                      </Grid>
                    )}
                    <Grid item justifyContent="flex-end">
                      <SrButton
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting}
                      >
                        {translate("common.saveChanges")}
                      </SrButton>
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>
          ) : (
            <div className={classes.loaderWrapper}>
              <CircularProgress />
            </div>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default Receipt;
